import { Link } from 'react-router'
import { useMediaQuery } from 'usehooks-ts'

import type { LandingHeader as LandingHeaderConfig } from '@entities/LandingPage'
import { useLocationStore } from '@entities/Location'

import { CabinetLink } from '@features/auth'
import { CartLink } from '@features/cart'
import { resolveLandingTarget } from '@features/landing-page-builder'
import { LocationPicker } from '@features/location-picker'
import { SearchInput } from '@features/search'
import { SwitchLocale } from '@features/switch-locale'
import { TopMenu } from '@features/top-menu'
import { TopMenuContacts } from '@features/top-menu-contacts'
import { TopMenuNavigation } from '@features/top-menu-navigation'

import { URL_MAIN_PAGE } from '@shared/contants'
import { cn } from '@shared/lib/cn'
import Container from '@shared/ui/container'

import MobileSearchBar from './MobileSearchBar'
import { useCitySelect } from '../model/useCitySelect'

import styles from './styles.module.scss'

interface LandingHeaderProps {
  header?: LandingHeaderConfig | null;
}

/*
  Custom header for a landing (header.variant === 'custom').

  Keeps the site's top bar — city, nav links, phone, locale — since those are
  site-wide utilities a visitor still needs. What it drops is the shop browsing
  chrome: the catalog menu and the topline scrollable menu (handled in the
  layout), so the main row is just logo + search + login + cart.

  The logo comes from header.logo.image, or its title as text when there is no
  creative; it links to header.logo.target when one is configured, otherwise
  home. Search is shown only when header.search.enabled, and is narrowed to the
  landing's assortment via header.search.filter (with categoryId as fallback).
*/
export default function LandingHeader ({ header }: LandingHeaderProps) {
  const isTablet = useMediaQuery('(max-width: 1179.9px)')
  const isMobile = useMediaQuery('(max-width: 719.9px)')

  const { currentCity } = useLocationStore()
  const handleCitySelect = useCitySelect()

  const logo = header?.logo
  const search = header?.search

  const logoTarget = resolveLandingTarget(logo?.target)
  const logoContent = logo?.image
    ? <img className={styles.landing__logoImage} src={logo.image} alt={logo.title ?? ''} />
    : <span className={styles.landing__logoTitle}>{logo?.title}</span>

  const hasLogo = Boolean(logo?.image || logo?.title)
  const searchEnabled = search?.enabled === true

  return (
    <>
      {/*
        Same top bar as the site header, and hidden on tablet down for the same
        reason — its contents move into the burger/tab bar there.
      */}
      {!isTablet && (
        <TopMenu
          leftSection={<LocationPicker currentCity={currentCity} onCitySelect={handleCitySelect} />}
          rightSection={<><TopMenuNavigation /><TopMenuContacts /></>}
          endSection={<SwitchLocale />}
        />
      )}

      <div className={cn(styles.header, styles.landing)}>
        <Container>
          <div className={styles.header__inner}>
            <div className={styles.landing__grid}>
              {hasLogo && (
                logoTarget?.external ? (
                  <a
                    className={styles.landing__logo}
                    href={logoTarget.href}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    {logoContent}
                  </a>
                ) : (
                  <Link className={styles.landing__logo} to={logoTarget?.href ?? URL_MAIN_PAGE}>
                    {logoContent}
                  </Link>
                )
              )}

              {searchEnabled && (
                isMobile ? (
                  <MobileSearchBar className={styles.landing__search} />
                ) : (
                  <SearchInput
                    className={styles.landing__search}
                    placeholder={search?.placeholder ?? undefined}
                    filter={search?.filter ?? undefined}
                    categoryId={search?.categoryId ?? undefined}
                  />
                )
              )}

              <CabinetLink className={styles.landing__cabinet} />
              <CartLink className={styles.landing__cart} />
            </div>
          </div>
        </Container>
      </div>
    </>
  )
}
